import React from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '../layouts/DashboardLayout'
import { Send, CheckCircle2, CalendarCheck, Wallet, TrendingDown } from 'lucide-react'

const R = (v) => 'R' + Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

const STATE = {
  sent: 'bg-brand-surface text-brand-muted border-brand-border',
  accepted: 'bg-amber-50 text-amber-800 border-amber-200',
  booked: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
}

function Tile({ icon: Icon, label, big, sub, tone = 'ink' }) {
  const toneCls = tone === 'good' ? 'text-emerald-600' : tone === 'warn' ? 'text-amber-600' : 'text-brand-ink'
  return (
    <div className="rounded-2xl border border-brand-border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-brand-muted"><Icon size={16} /><span className="text-xs font-semibold uppercase tracking-wide">{label}</span></div>
      <div className={`mt-2 text-2xl font-bold tabular-nums ${toneCls}`}>{big}</div>
      {sub && <div className="mt-0.5 text-xs text-brand-muted">{sub}</div>}
    </div>
  )
}

export default function CaseAcceptance({ days = 90, funnel = {}, journeys = {}, reasons = [], recent = [] }) {
  const setDays = (d) => router.get('/case-acceptance', { days: d }, { preserveState: false, preserveScroll: true })
  const maxReason = Math.max(1, ...reasons.map((r) => r.count))

  return (
    <DashboardLayout>
      <Head title="Case acceptance" />
      <div className="mx-auto flex max-w-5xl flex-col gap-5 p-4 sm:p-6">

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-brand-ink">Case acceptance</h1>
            <p className="text-sm text-brand-muted">Turning estimates and treatment plans into booked appointments.</p>
          </div>
          <div className="flex overflow-hidden rounded-lg border border-brand-border text-sm">
            {[[30, '30 days'], [90, '90 days'], [0, 'All time']].map(([d, l]) => (
              <button key={d} onClick={() => setDays(d)}
                      className={`px-3 py-1.5 font-medium transition ${Number(days) === d ? 'bg-brand-primary text-white' : 'bg-white text-brand-ink hover:bg-brand-surface'}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile icon={Send} label="Plans sent" big={funnel.sent?.count ?? 0} sub={`${R(funnel.sent?.value)} of treatment`} />
          <Tile icon={CheckCircle2} label="Accepted" big={`${funnel.accepted?.pct ?? 0}%`} sub={`${funnel.accepted?.count ?? 0} · ${R(funnel.accepted?.value)}`} tone="good" />
          <Tile icon={CalendarCheck} label="Booked" big={`${funnel.booked?.pct ?? 0}%`} sub={`${funnel.booked?.count ?? 0} · ${R(funnel.booked?.value)}`} tone="good" />
          <Tile icon={Wallet} label="Un-booked pipeline" big={R(funnel.unbooked_value)} sub="sent but not yet booked" tone="warn" />
        </div>

        {/* Funnel bar */}
        <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-brand-muted">The funnel</p>
          {[
            { label: 'Sent', n: funnel.sent?.count ?? 0, cls: 'bg-brand-muted' },
            { label: 'Accepted', n: funnel.accepted?.count ?? 0, cls: 'bg-amber-400' },
            { label: 'Booked', n: funnel.booked?.count ?? 0, cls: 'bg-emerald-500' },
          ].map((row) => {
            const base = funnel.sent?.count || 1
            return (
              <div key={row.label} className="mb-2 flex items-center gap-3">
                <span className="w-20 shrink-0 text-sm text-brand-ink">{row.label}</span>
                <div className="h-5 flex-1 overflow-hidden rounded bg-brand-surface">
                  <div className={`h-full ${row.cls}`} style={{ width: `${Math.min(100, (row.n / base) * 100)}%` }} />
                </div>
                <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-brand-ink">{row.n}</span>
              </div>
            )
          })}
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {/* Why they don't proceed */}
          <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-brand-muted"><TrendingDown size={16} /><p className="text-xs font-bold uppercase tracking-wider">Why plans stall</p></div>
            {reasons.length === 0 ? (
              <p className="text-sm text-brand-muted">No decline feedback in this period. When a patient taps a reason on their plan, it shows here.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {reasons.map((r) => (
                  <div key={r.reason} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 truncate text-sm text-brand-ink">{r.label}</span>
                    <div className="h-4 flex-1 overflow-hidden rounded bg-brand-surface">
                      <div className="h-full bg-red-400" style={{ width: `${(r.count / maxReason) * 100}%` }} />
                    </div>
                    <span className="w-8 shrink-0 text-right text-sm font-semibold tabular-nums text-brand-ink">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs text-brand-muted">Treatment journeys — sent {journeys.sent ?? 0} · accepted {journeys.accepted ?? 0} · declined {journeys.declined ?? 0}</p>
          </div>

          {/* Recent activity */}
          <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-brand-muted">Recent</p>
            {recent.length === 0 ? (
              <p className="text-sm text-brand-muted">Nothing sent in this period yet.</p>
            ) : (
              <div className="flex flex-col divide-y divide-brand-border">
                {recent.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 py-2 text-sm">
                    <span className="w-10 shrink-0 text-[11px] font-medium uppercase text-brand-muted">{r.kind === 'Journey' ? 'Plan' : 'Est'}</span>
                    <span className="flex-1 truncate text-brand-ink">{r.title || r.ref}{r.reason ? <span className="text-red-600"> · {r.reason}</span> : null}</span>
                    <span className="shrink-0 tabular-nums text-brand-muted">{R(r.value)}</span>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${STATE[r.state] || STATE.sent}`}>{r.state}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-brand-muted">A plan counts as “booked” once an appointment is linked to its estimate. Chase the un-booked pipeline — that’s new-patient revenue waiting.</p>
      </div>
    </DashboardLayout>
  )
}
